import { modals } from '@mantine/modals'
import usePersistentForm from '@v2/commons/hooks/forms/usePersistentValuesForm'
import usePaginate from '@v2/commons/hooks/pagination/usePagination.hook'
import showModalNotificationError from '@v2/commons/modals/ModalNotification/showModalNotificationError'
import { filterUndefinedValues } from '@v2/commons/utils/filterUndefinedValues'
import { useRouter } from '@v2/commons/utils/navigation'
import { DEFAULT_ROUTES } from '@v2/domains/routing/types/routing.types'
import { getRoutePath } from '@v2/domains/routing/utils/route.utils'
import { useEffect, useRef, useState } from 'react'

import { useGetUsersQuery } from '../api/user.api'
import { IUsersFilter, OrderByType } from '../user.types'
import { buildOrderByString, buildUsersQueryParams } from '../utils/user.mapper'

export const useUsers = () => {
  const {
    handlePaginate,
    organizationId,
    paginationLimitField,
    tablePage,
    page,
  } = usePaginate()
  const isProgrammaticFormUpdateRef = useRef<boolean>(true)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const [filterOrderBy, setFilterOrderBy] = useState<{
    field: string
    orderBy: OrderByType
  }>({ field: '', orderBy: undefined })

  const usersFiltersForm = usePersistentForm<IUsersFilter>({
    localStoragePath: `${organizationId}-users`,
    isProgrammaticFormUpdateRef,
    formConfig: {
      initialValues: {},
      mode: 'uncontrolled',
      onValuesChange: (values, previousValues) => {
        handlePaginate(1)
        const arePersistedFilters = !!Object.keys(values).length
        const didUserClearFilters = !!Object.keys(previousValues).length
        if (!didUserClearFilters) {
          setFiltersOpen(arePersistedFilters ? true : false)
        }
        return values
      },
    },
  })

  const getQueryFilters = () => {
    const filterValues = { ...usersFiltersForm.getValues() }
    return filterUndefinedValues(
      filterValues as Record<
        string,
        string | number | boolean | object | null | undefined
      >,
    )
  }

  const { data, isLoading, isSuccess, isFetching, isError, refetch } =
    useGetUsersQuery(
      buildUsersQueryParams(
        {
          orderBy: buildOrderByString(
            filterOrderBy.field,
            filterOrderBy.orderBy,
          ),
          ...getQueryFilters(),
        },
        organizationId ?? '',
        page,
        parseInt(paginationLimitField.getValue()),
      ),
    )

  useEffect(() => {
    if (isError) {
      showModalNotificationError({
        subtitle: 'Error fetching users',
        buttonText: 'Retry',
        onClickBtn: () => refetch(),
      })
    } else {
      modals.closeAll()
    }
  }, [isError])

  const router = useRouter()

  const handleOrderBy = (field: string, orderBy: OrderByType) => {
    setFilterOrderBy({ field, orderBy })
  }

  const handleEditUser = (userId: string) => {
    router.push(
      `${getRoutePath(DEFAULT_ROUTES.ADMINISTRATION_USERS)}/${userId}`,
    )
  }

  const handleCreateUser = () => {
    router.push(`${getRoutePath(DEFAULT_ROUTES.ADMINISTRATION_USERS)}/create`)
  }

  return {
    users: data,
    filterOrderBy,
    isLoading,
    isFetching,
    isSuccess,
    handlePaginate,
    handleEditUser,
    handleOrderBy,
    handleCreateUser,
    paginationLimitField,
    tablePage,
    isError,
    usersFiltersForm,
    isProgrammaticFormUpdateRef,
    organizationId,
    setFiltersOpen,
    filtersOpen,
  }
}
